import { useAccount } from "@qw/auth";
import {
  useInboxConversationUpdatedSubscription,
  useInboxLabelUnreadCountsQuery,
  useInboxMessageCreatedSubscription,
  useInboxMessageReadySubscription,
} from "@qw/graphql";
import { KnownInboxLabel } from "@qw/qw-common";
import { useMetadata } from "@qw/ui";
import { useCallback, useMemo } from "react";
import { Outlet, useLocation } from "react-router";
import { useDocumentTitle } from "usehooks-ts";
import { matchLabelMenuItem } from "../constants/known-labels";
import { useInboxQueryStates } from "../providers/inbox-query-states-provider";

const INBOX_UNREAD_COUNT_LABELS = [KnownInboxLabel.INBOX];
const DEFAULT_ROOT_TITLE = "QWIB";

const isInboxPath = (pathname: string) => pathname === "/inbox" || pathname.startsWith("/inbox/");
const isInboxLabel = (labels: string[]) => labels.length === 1 && labels[0] === KnownInboxLabel.INBOX;
const matchesSectionPath = (pathname: string, section: string) =>
  pathname === section || pathname.startsWith(`${section}/`);

const getRootTitle = (pathname: string, labels: string[]) => {
  if (isInboxPath(pathname)) {
    return matchLabelMenuItem(labels).text;
  }

  if (matchesSectionPath(pathname, "/playbooks")) {
    return "Playbooks";
  }

  if (matchesSectionPath(pathname, "/classifications")) {
    return "Classifications";
  }

  if (matchesSectionPath(pathname, "/products")) {
    return "Products";
  }

  if (matchesSectionPath(pathname, "/carriers")) {
    return "Carriers";
  }

  return DEFAULT_ROOT_TITLE;
};

const useInboxUnreadCount = (enabled: boolean) => {
  const { userInbox } = useAccount();
  const inboxEmail = userInbox?.email ?? "";

  const { data, refetch } = useInboxLabelUnreadCountsQuery({
    variables: { input: { labels: INBOX_UNREAD_COUNT_LABELS } },
    fetchPolicy: "cache-and-network",
    skip: !enabled,
  });

  const refetchUnreadCount = useCallback(() => {
    if (enabled) {
      void refetch();
    }
  }, [enabled, refetch]);

  useInboxMessageCreatedSubscription({
    variables: { email: inboxEmail },
    onData: refetchUnreadCount,
    skip: !enabled || !inboxEmail,
  });

  useInboxMessageReadySubscription({
    variables: { email: inboxEmail },
    onData: refetchUnreadCount,
    skip: !enabled || !inboxEmail,
  });

  useInboxConversationUpdatedSubscription({
    variables: { email: inboxEmail },
    onData: refetchUnreadCount,
    skip: !enabled || !inboxEmail,
  });

  return data?.inboxLabelUnreadCounts.find(({ label }) => label === KnownInboxLabel.INBOX)?.count ?? 0;
};

export const AppLayout = () => {
  const { metadata } = useMetadata();
  const { pathname } = useLocation();
  const { conversationId, labels } = useInboxQueryStates();
  const rootTitle = getRootTitle(pathname, labels);
  const shouldShowUnreadCount =
    pathname === "/inbox" && isInboxLabel(labels) && !conversationId && metadata.length === 1;
  const inboxUnreadCount = useInboxUnreadCount(shouldShowUnreadCount);

  const title = useMemo(
    () =>
      metadata
        .map((page, index) => (index === 0 ? rootTitle : page.title))
        .reverse()
        .join(" : "),
    [metadata, rootTitle]
  );
  const titleWithUnreadCount = inboxUnreadCount > 0 && shouldShowUnreadCount ? `(${inboxUnreadCount}) ${title}` : title;

  useDocumentTitle(titleWithUnreadCount);

  return (
    <div className="flex flex-col h-dvh overflow-hidden w-dvw">
      <Outlet />
    </div>
  );
};
